import type {
  QueryKey,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback } from 'react';
import type {
  UploadFile,
  UploadState,
} from '@pflegenavi/shared-frontend/platform';
import {
  makeApiProvider,
  useApiQuery,
  usePrefetchApiQuery,
  useSuspenseApiQuery,
  useUploadFile,
} from '@pflegenavi/shared-frontend/platform';
import type {
  CreateOfflineMandateBody,
  CreateResidentParams,
  CreateResidentResult,
  GetAllResidentsPhoenixParams,
  IResidentPhoenixApi,
  MandateCreateResponse,
  MandateDeleteResponse,
  MandateDetailsResponse,
  MandateDocumentsResponse,
  MandateRefreshResponse,
  MandateValidationResponse,
  PaymentScheduleResponse,
  ResidentRecurringItemsPhoenixResponse,
  UpdateResidentFamilyMemberDto,
} from './api';
import { ResidentPhoenixApi } from './api';
import {
  ResidentState,
  PaymentInitiatedBy,
  type PaymentSchedule,
  type FamilyMemberPhoenix,
  type GetAllResidentsResult,
  type PaymentInitiationDto,
  type Resident,
  type ShowResidentDto,
  type UpdateResidentCashPayerStateDto,
} from '@pflegenavi/shared/api';
import { PAYMENT_STATUS_KEY } from '@pflegenavi/shared/constants';
import {
  invalidateResident,
  invalidateResidentsKeys,
  UPDATE_RESIDENT_CASH_PAYER_STATUS_KEY,
} from '../resident/queryKeys';
import { PAYMENTS_INFO_KEY } from '../payment-phoenix';
import {
  RESIDENTS_PHOENIX_KEY,
  RESIDENT_RECURRING_ITEMS_PHOENIX_KEY,
  SHOW_RESIDENT_KEY,
} from './queryKeys';
import { CASH_LISTS_PHOENIX_KEY } from '../cash-management-phoenix/queryKeys';
import { useGetPaymentProcessor } from '../nursing-home-phoenix';

export type {
  InitialBalance,
  ResidentRecurringItemPhoenix,
  ResidentRecurringItemsPhoenixResponse,
} from './api';

const ApiContext = createContext<IResidentPhoenixApi | undefined>(undefined);
const {
  useApi: useResidentPhoenixApi,
  ApiProvider: ResidentPhoenixApiProvider,
} = makeApiProvider({
  name: 'ResidentPhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) => {
    return new ResidentPhoenixApi(tenantId, auth, apiUrl);
  },
});

export {
  useResidentPhoenixApi,
  ResidentPhoenixApiProvider,
  ApiContext as ResidentPhoenixApiContext,
  IResidentPhoenixApi,
};

const UPDATE_RESIDENT_KEY: QueryKey = ['update-resident-key'];

const DELETE_RESIDENT_FAMILY_MEMBER_KEY = (residentId: string) => [
  'delete-resident-family-member',
  residentId,
];

const UPDATE_RESIDENT_FAMILY_MEMBER_KEY = (residentId: string) => [
  'update-resident-family-member',
  residentId,
];

const RESIDENT_STALE_TIME = 1000 * 60 * 5; // 5 minutes

const selectResidents = (
  dataIn: GetAllResidentsResult
): GetAllResidentsResult => {
  const data = dataIn.data.map((resident) => ({
    ...resident,
    balance: {
      ...resident.balance,
      threshold_due_date:
        resident.balance.threshold_due_date &&
        new Date(resident.balance.threshold_due_date),
      negative_due_date:
        resident.balance.negative_due_date &&
        new Date(resident.balance.negative_due_date),
    },

    resident_family_member_balances:
      resident.resident_family_member_balances.map((balance) => ({
        ...balance,
        threshold_due_date:
          balance.threshold_due_date && new Date(balance.threshold_due_date),
        negative_due_date:
          balance.negative_due_date && new Date(balance.negative_due_date),
      })),
    nursing_home_residents: {
      ...resident.nursing_home_residents,
      entry_date: new Date(resident.nursing_home_residents.entry_date),
      exit_date:
        resident.nursing_home_residents.exit_date &&
        new Date(resident.nursing_home_residents.exit_date),
    },
    settlement: {
      ...resident.settlement,
      settlement_date:
        resident.settlement?.settlement_date &&
        new Date(resident.settlement.settlement_date),
    },
  }));

  return {
    ...dataIn,
    data: data,
  };
};

/**
 * Heavily based on useResidents. Since we need to add additionalFields without breaking other queries
 * @param nursingHomeId
 * @param options
 */
export const useAllResidentsPhoenix = (
  nursingHomeId: string | undefined,
  options?: Omit<
    UseQueryOptions<GetAllResidentsResult, unknown>,
    'queryFn' | 'queryKey'
  >,
  params?: Omit<GetAllResidentsPhoenixParams, 'nursingHomeId'>
): UseQueryResult<GetAllResidentsResult, unknown> => {
  const result = useApiQuery(
    useResidentPhoenixApi,
    RESIDENTS_PHOENIX_KEY(nursingHomeId ?? '', params),
    (api) => {
      if (!nursingHomeId) {
        throw new Error('nursingHomeId is required');
      }
      return api.getAllResidents({ params: { nursingHomeId, ...params } });
    },
    {
      enabled: nursingHomeId !== undefined,
      staleTime: RESIDENT_STALE_TIME,
      select: selectResidents,
      ...options,
    }
  );

  return result;
};

export const useResidentFromListPhoenix = (
  id: string,
  nursingHomeId: string | undefined
): UseQueryResult<
  GetAllResidentsResult['data'][number] | undefined,
  unknown
> => {
  const select = useCallback(
    (data: GetAllResidentsResult) => {
      return selectResidents(data).data.find((resident) => resident.id === id);
    },
    [id]
  );

  // @ts-expect-error // Type mismatch
  return useAllResidentsPhoenix(nursingHomeId, {
    // @ts-expect-error // Type mismatch
    select,
  });
};

export const usePrefetchResidentPhoenix = (
  residentId: string | undefined
): void => {
  const getResident = useCallback(
    (api: IResidentPhoenixApi) => {
      return api.showResident({ params: { residentId: residentId ?? '' } });
    },
    [residentId]
  );

  return usePrefetchApiQuery(
    useResidentPhoenixApi,
    SHOW_RESIDENT_KEY(residentId ?? ''),
    getResident,
    residentId !== undefined
  );
};

export const useActiveAndExitedResidentsPhoenix = (
  nursingHomeId: string | undefined,
  options?: Omit<
    UseQueryOptions<GetAllResidentsResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetAllResidentsResult, unknown> => {
  return useAllResidentsPhoenix(nursingHomeId, options, {
    states: [ResidentState.active, ResidentState.exited],
  });
};

export const useActiveExitedAndAwaitingSettlementResidents = (
  nursingHomeId: string | undefined,
  options?: Omit<
    UseQueryOptions<GetAllResidentsResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetAllResidentsResult, unknown> => {
  return useAllResidentsPhoenix(nursingHomeId, options, {
    states: [
      ResidentState.active,
      ResidentState.exited,
      ResidentState.awaitingSettlement,
    ],
  });
};

/**
 * Residents that can be selected when creating receipts or receipt batches.
 *
 * Mangopay tenants additionally allow residents awaiting settlement, since
 * their move-out/death flows keep receipts creatable until archival. Stripe
 * tenants are limited to active and exited residents.
 */
export const useReceiptCreationResidents = (
  nursingHomeId: string | undefined,
  options?: Omit<
    UseQueryOptions<GetAllResidentsResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetAllResidentsResult, unknown> => {
  const paymentProcessor = useGetPaymentProcessor();
  const states =
    paymentProcessor === 'mangopay'
      ? [
          ResidentState.active,
          ResidentState.exited,
          ResidentState.awaitingSettlement,
        ]
      : [ResidentState.active, ResidentState.exited];

  return useAllResidentsPhoenix(nursingHomeId, options, { states });
};

export const useActiveResidentsPhoenix = (
  nursingHomeId: string | undefined,
  options?: Omit<
    UseQueryOptions<GetAllResidentsResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetAllResidentsResult, unknown> => {
  return useAllResidentsPhoenix(nursingHomeId, options, {
    states: [ResidentState.active],
  });
};

export const useInactiveResidentsPhoenix = (
  nursingHomeId: string | undefined,
  options?: Omit<
    UseQueryOptions<GetAllResidentsResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<GetAllResidentsResult, unknown> => {
  return useAllResidentsPhoenix(nursingHomeId, options, {
    states: [ResidentState.archived],
  });
};

/**
 * Heavily based on useResidents. Since we need to add additionalFields without breaking other queries
 * @param nursingHomeId
 * @param options
 */
export const useAllResidentsPhoenixSuspense = (
  nursingHomeId: string,
  options?: Omit<
    UseSuspenseQueryOptions<GetAllResidentsResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseSuspenseQueryResult<GetAllResidentsResult, unknown> => {
  const result = useSuspenseApiQuery(
    useResidentPhoenixApi,
    RESIDENTS_PHOENIX_KEY(nursingHomeId),
    (api) => {
      return api.getAllResidents({ params: { nursingHomeId } });
    },
    {
      staleTime: RESIDENT_STALE_TIME,
      ...options,
      select: selectResidents,
    }
  );

  return result;
};

export const useCreateResidentPhoenix = (): UseMutationResult<
  CreateResidentResult,
  unknown,
  CreateResidentParams
> => {
  const api = useResidentPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation<CreateResidentResult, unknown, CreateResidentParams>({
    mutationKey: ['CREATE_RESIDENT_PHOENIX'],
    mutationFn: (data) => api.createResident(data),
    onSuccess: (_data, variables) => {
      return invalidateResidentsKeys(
        queryClient,
        variables.body.nursing_home_id
      );
    },
  });
};

export function selectShowResidentDto(data: ShowResidentDto): ShowResidentDto {
  return {
    data: {
      ...data.data,
      resident: {
        ...data.data.resident,
        balance: {
          ...data.data.resident.balance,
          negative_due_date:
            data.data.resident.balance.negative_due_date &&
            new Date(data.data.resident.balance.negative_due_date),
          threshold_due_date:
            data.data.resident.balance.threshold_due_date &&
            new Date(data.data.resident.balance.threshold_due_date),
        },
        birthdate:
          data.data.resident.birthdate &&
          new Date(data.data.resident.birthdate),
        created_on:
          data.data.resident.created_on &&
          new Date(data.data.resident.created_on),
        nursing_home_residents: {
          ...data.data.resident.nursing_home_residents,
          entry_date: new Date(
            data.data.resident.nursing_home_residents.entry_date
          ),
          exit_date:
            data.data.resident.nursing_home_residents.exit_date &&
            new Date(data.data.resident.nursing_home_residents.exit_date),
        },
        resident_family_member_balances:
          data.data.resident.resident_family_member_balances.map((balance) => ({
            ...balance,
            threshold_due_date:
              balance.threshold_due_date &&
              new Date(balance.threshold_due_date),
            negative_due_date:
              balance.negative_due_date && new Date(balance.negative_due_date),
          })),
        settlement: {
          ...data.data.resident.settlement,
          settlement_date:
            data.data.resident.settlement?.settlement_date &&
            new Date(data.data.resident.settlement.settlement_date),
        },
        pending_receipts: data.data.resident.pending_receipts?.map(
          (receipt) => ({
            ...receipt,
            receipt_date: receipt.receipt_date
              ? new Date(receipt.receipt_date)
              : receipt.receipt_date,
            submission_date: receipt.submission_date
              ? new Date(receipt.submission_date)
              : receipt.submission_date,
          })
        ),
      },
      resident_family_member_balances:
        data.data.resident_family_member_balances.map((balance) => {
          return {
            ...balance,
            threshold_due_date:
              balance.threshold_due_date &&
              new Date(balance.threshold_due_date),
            negative_due_date:
              balance.negative_due_date && new Date(balance.negative_due_date),
          };
        }),
    },
  };
}

export const useShowResidentPhoenix = (
  residentId: string,
  options?: Omit<
    UseQueryOptions<ShowResidentDto, unknown, ShowResidentDto>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<ShowResidentDto, unknown> => {
  const select = useCallback(selectShowResidentDto, []);

  return useApiQuery(
    useResidentPhoenixApi,
    SHOW_RESIDENT_KEY(residentId),
    (api) => {
      return api?.showResident({
        params: {
          residentId,
        },
      });
    },
    {
      ...options,
      select,
      enabled: Boolean(residentId),
    }
  );
};

export const useShowResidentForFamilyMemberPhoenix = (
  residentId: string,
  tenant: string,
  options?: Omit<
    UseQueryOptions<ShowResidentDto, unknown, ShowResidentDto>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<ShowResidentDto, unknown> => {
  const select = useCallback(selectShowResidentDto, []);

  return useApiQuery(
    useResidentPhoenixApi,
    SHOW_RESIDENT_KEY(residentId),
    (api) => {
      return api?.showResidentForFamilyMember({
        params: {
          residentId,
          tenant,
        },
      });
    },
    {
      ...options,
      select,
      enabled: Boolean(residentId) && Boolean(tenant),
    }
  );
};

export const useAddNewFamilyMemberPhoenix = (
  residentId: string
): UseMutationResult<
  {
    status: 'Created';
  },
  unknown,
  FamilyMemberPhoenix
> => {
  const api = useResidentPhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<
    {
      status: 'Created';
    },
    unknown,
    FamilyMemberPhoenix
  >({
    mutationKey: UPDATE_RESIDENT_KEY,
    mutationFn: (data) => {
      return api.addNewFamilyMember({
        params: { residentId },
        body: {
          data,
        },
      });
    },
    onSuccess: async () => {
      return await Promise.all([
        queryClient.invalidateQueries({
          queryKey: PAYMENT_STATUS_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: PAYMENTS_INFO_KEY(residentId),
        }),
        invalidateResident(queryClient, residentId, undefined),
      ]);
    },
  });
};

export const useResendFamilyMemberWelcomeEmail = (): UseMutationResult<
  void,
  unknown,
  {
    residentId: string;
    familyMemberId: string;
  }
> => {
  const api = useResidentPhoenixApi();
  const result = useMutation<
    void,
    unknown,
    {
      residentId: string;
      familyMemberId: string;
    }
  >({
    mutationKey: ['RESEND_FAMILY_MEMBER_WELCOME_EMAIL'],
    mutationFn: (data) =>
      api.resendFamilyMemberWelcomeEmail({
        params: data,
      }),
  });
  return result;
};

export const useDeleteFamilyMemberForGivenResidentPhoenix = (
  residentId: string
): UseMutationResult<
  {
    success: boolean;
  },
  unknown,
  {
    familyMemberId: string;
  }
> => {
  const api = useResidentPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation<
    {
      success: boolean;
    },
    unknown,
    {
      familyMemberId: string;
    }
  >({
    mutationKey: DELETE_RESIDENT_FAMILY_MEMBER_KEY(residentId),
    mutationFn: (data) =>
      api.deleteFamilyMemberForGivenResidentPhoenix({
        params: {
          familyMemberId: data.familyMemberId,
          residentId: residentId,
        },
      }),
    onSuccess: async () => {
      return await Promise.all([
        queryClient.invalidateQueries({
          queryKey: PAYMENT_STATUS_KEY(residentId),
        }),
        invalidateResident(queryClient, residentId, undefined),
      ]);
    },
  });
};

export const useUpdateFamilyMemberForGivenResidentPhoenix = (
  residentId: string
): UseMutationResult<
  {
    data: UpdateResidentFamilyMemberDto;
  },
  unknown,
  UpdateResidentFamilyMemberDto
> => {
  const api = useResidentPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation<
    {
      data: UpdateResidentFamilyMemberDto;
    },
    unknown,
    UpdateResidentFamilyMemberDto
  >({
    mutationKey: UPDATE_RESIDENT_FAMILY_MEMBER_KEY(residentId),
    mutationFn: (data) =>
      api.updateFamilyMemberForGivenResidentPhoenix({
        params: {
          familyMemberId: data.user_id,
          residentId: residentId,
        },
        body: {
          data: data,
        },
      }),
    onSuccess: async () => {
      return await Promise.all([
        queryClient.invalidateQueries({
          queryKey: PAYMENTS_INFO_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: PAYMENT_STATUS_KEY(residentId),
        }),
        invalidateResident(queryClient, residentId, undefined),
      ]);
    },
  });
};

export const useUpdateResidentCashPayerStatus = (
  residentId: string,
  nursingHomeId: string
): UseMutationResult<
  { data: { resident: Resident } },
  unknown,
  UpdateResidentCashPayerStateDto
> => {
  const api = useResidentPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation<
    { data: { resident: Resident } },
    unknown,
    UpdateResidentCashPayerStateDto
  >({
    mutationKey: [UPDATE_RESIDENT_CASH_PAYER_STATUS_KEY],
    mutationFn: (data) => {
      return api.updateResidentCashPayerStatus({
        params: { residentId },
        body: { data },
      });
    },
    onSuccess: () => {
      return Promise.all([
        invalidateResident(queryClient, residentId, nursingHomeId),
        invalidateResidentsKeys(queryClient, nursingHomeId),
        queryClient.invalidateQueries({
          queryKey: SHOW_RESIDENT_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LISTS_PHOENIX_KEY(),
        }),
      ]);
    },
  });
};

export const useValidateOfflineMandate = (
  residentId: string
): UseMutationResult<
  MandateValidationResponse,
  unknown,
  { recipient_data: Record<string, unknown>; country?: string }
> => {
  const api = useResidentPhoenixApi();
  return useMutation({
    mutationKey: ['validate-offline-mandate', residentId],
    mutationFn: (data) =>
      api.validateOfflineMandate({
        params: { residentId },
        body: data,
      }),
  });
};

export const useCreateOfflineMandate = (
  residentId: string
): UseMutationResult<
  MandateCreateResponse,
  unknown,
  CreateOfflineMandateBody
> => {
  const api = useResidentPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['create-offline-mandate', residentId],
    mutationFn: (data) =>
      api.createOfflineMandate({
        params: { residentId },
        body: data,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: SHOW_RESIDENT_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: MANDATE_DOCUMENTS_KEY(residentId),
        }),
        invalidateResident(queryClient, residentId, undefined),
      ]);
    },
  });
};

export const useDeleteOfflineMandate = (
  residentId: string
): UseMutationResult<MandateDeleteResponse, unknown, void> => {
  const api = useResidentPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['delete-offline-mandate', residentId],
    mutationFn: () =>
      api.deleteOfflineMandate({
        params: { residentId },
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: SHOW_RESIDENT_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: PAYMENTS_INFO_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: MANDATE_DOCUMENTS_KEY(residentId),
        }),
        invalidateResident(queryClient, residentId, undefined),
      ]);
    },
  });
};

export const useRefreshOfflineMandate = (
  residentId: string
): UseMutationResult<MandateRefreshResponse, unknown, void> => {
  const api = useResidentPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['refresh-offline-mandate', residentId],
    mutationFn: () =>
      api.refreshOfflineMandate({
        params: { residentId },
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: SHOW_RESIDENT_KEY(residentId),
        }),
        invalidateResident(queryClient, residentId, undefined),
      ]);
    },
  });
};

const OFFLINE_MANDATE_DETAILS_KEY = (residentId: string): QueryKey => [
  'offline-mandate-details',
  residentId,
];

export const useGetOfflineMandateDetails = (
  residentId: string
): UseQueryResult<MandateDetailsResponse, unknown> => {
  const api = useResidentPhoenixApi();
  return useQuery({
    queryKey: OFFLINE_MANDATE_DETAILS_KEY(residentId),
    queryFn: () =>
      api.getOfflineMandateDetails({
        params: { residentId },
      }),
    enabled:
      Boolean(residentId) &&
      !api?.authContext.initializing &&
      api?.authContext.getTokenImmediate() !== undefined,
    staleTime: RESIDENT_STALE_TIME,
  });
};

export const MANDATE_DOCUMENTS_KEY = (residentId: string): QueryKey => [
  'mandate-documents',
  residentId,
];

/**
 * Uploads a hand-written SEPA document image to S3 via a presigned url and
 * returns the generated image id, to be passed to `useCreateOfflineMandate`.
 *
 * @example
 * const { upload } = useUploadMandateDocument(residentId, v4, computeMd5HashOfFile);
 */
export const useUploadMandateDocument = (
  residentId: string,
  uuid: () => string,
  computeMd5Hash: (file: File) => Promise<string>
): { upload: UploadFile; uploadState: UploadState | undefined } => {
  const api = useResidentPhoenixApi();

  const getUploadUrl = useCallback(
    (imageId: string, fileType: string, md5Hash: string) =>
      api.getMandateDocumentUploadUrl(residentId, imageId, fileType, md5Hash),
    [api, residentId]
  );

  return useUploadFile({ uuid, getUploadUrl, computeMd5Hash });
};

/**
 * Presigned download urls for the resident's stored SEPA document images.
 * The endpoint responds 403 for mandates set up by a family member, so callers
 * pass `enabled: false` in that case.
 *
 * @example
 * useGetMandateDocuments(residentId, mandateSetupByFamilyMemberId == null)
 */
export const useGetMandateDocuments = (
  residentId: string,
  enabled = true
): UseQueryResult<MandateDocumentsResponse, unknown> => {
  const api = useResidentPhoenixApi();
  return useQuery({
    queryKey: MANDATE_DOCUMENTS_KEY(residentId),
    queryFn: () => api.getMandateDocuments({ params: { residentId } }),
    enabled:
      enabled &&
      Boolean(residentId) &&
      !api?.authContext.initializing &&
      api?.authContext.getTokenImmediate() !== undefined,
    staleTime: RESIDENT_STALE_TIME,
  });
};

const PAYMENT_SCHEDULE_KEY = (residentId: string): QueryKey => [
  'payment-schedule',
  residentId,
];

function paymentScheduleResponseToDto(
  data: PaymentScheduleResponse
): PaymentInitiationDto | undefined {
  const ps = data.data;
  if (!ps.payment_initiation) {
    return undefined;
  }
  return {
    paymentInitiation: ps.payment_initiation as PaymentInitiatedBy,
    paymentSchedule:
      ps.payment_initiation === PaymentInitiatedBy.System
        ? {
            schedule: ps.payment_schedule as PaymentSchedule,
            dayOfMonth: ps.payment_schedule_day ?? undefined,
            payinAmount: ps.payment_schedule_payin_amount ?? undefined,
            maxWalletAmount: ps.payment_schedule_max_wallet_amount ?? undefined,
            maxAmount: ps.payment_schedule_max_amount ?? undefined,
          }
        : undefined,
  };
}

function paymentInitiationDtoToSnakeCase(
  dto: PaymentInitiationDto
): Record<string, unknown> {
  const result: Record<string, unknown> = {
    payment_initiation: dto.paymentInitiation,
  };
  if (dto.paymentSchedule) {
    result['payment_schedule'] = dto.paymentSchedule.schedule;
    result['payment_schedule_day'] = dto.paymentSchedule.dayOfMonth ?? null;
    result['payment_schedule_payin_amount'] =
      dto.paymentSchedule.payinAmount ?? null;
    result['payment_schedule_max_wallet_amount'] =
      dto.paymentSchedule.maxWalletAmount ?? null;
    result['payment_schedule_max_amount'] =
      dto.paymentSchedule.maxAmount ?? null;
  } else {
    // Explicitly null out schedule fields so the backend clears any previously
    // stored values (Ecto's get_field returns existing DB values for uncast fields)
    result['payment_schedule'] = null;
    result['payment_schedule_day'] = null;
    result['payment_schedule_payin_amount'] = null;
    result['payment_schedule_max_wallet_amount'] = null;
    result['payment_schedule_max_amount'] = null;
  }
  return result;
}

export const useGetPaymentSchedule = (
  residentId: string,
  options?: Omit<
    UseQueryOptions<
      PaymentScheduleResponse,
      unknown,
      PaymentInitiationDto | undefined
    >,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<PaymentInitiationDto | undefined, unknown> => {
  const select = useCallback(
    (data: PaymentScheduleResponse) => paymentScheduleResponseToDto(data),
    []
  );

  return useApiQuery(
    useResidentPhoenixApi,
    PAYMENT_SCHEDULE_KEY(residentId),
    (api) => {
      return api.getPaymentSchedule({
        params: { residentId },
      });
    },
    {
      ...options,
      select,
      enabled: Boolean(residentId),
    }
  );
};

export const useUpdatePaymentSchedule = (
  residentId: string
): UseMutationResult<
  PaymentScheduleResponse,
  unknown,
  PaymentInitiationDto
> => {
  const api = useResidentPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['update-payment-schedule', residentId],
    mutationFn: (data) => {
      return api.updatePaymentSchedule({
        params: { residentId },
        body: paymentInitiationDtoToSnakeCase(data),
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: PAYMENT_SCHEDULE_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: SHOW_RESIDENT_KEY(residentId),
        }),
        invalidateResident(queryClient, residentId, undefined),
      ]);
    },
  });
};

export const useRecurringItemsForResidentPhoenix = (
  residentId: string
): UseQueryResult<ResidentRecurringItemsPhoenixResponse, unknown> => {
  return useApiQuery(
    useResidentPhoenixApi,
    RESIDENT_RECURRING_ITEMS_PHOENIX_KEY(residentId),
    (api) => api.getRecurringItemsForResident({ params: { residentId } }),
    {
      enabled: Boolean(residentId),
    }
  );
};
